import {
  AccredibleResponsiveBreakpoint,
  AccredibleResponsiveHelper,
} from '@accredible-frontend-v2/utils/responsive-helper';
import { ConfigurableFocusTrapFactory, FocusMonitor } from '@angular/cdk/a11y';
import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import {
  DestroyRef,
  Directive,
  ElementRef,
  inject,
  input,
  OnDestroy,
  output,
  ViewContainerRef,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { merge, Observable, Subscription } from 'rxjs';
import { MobilePopUpComponent } from './mobile-pop-up.component';

@Directive({
  selector: '[accredibleMobilePopUpTriggerFor]',
  host: {
    '(window:resize)': 'onResize()',
    '(click)': 'toggleMenu()',
  },
})
export class PopUpMenuTriggerDirective implements OnDestroy {
  private readonly _configurableFocusTrapFactory: ConfigurableFocusTrapFactory = inject(
    ConfigurableFocusTrapFactory,
  );
  private readonly _overlay: Overlay = inject(Overlay);
  private readonly _viewContainerRef: ViewContainerRef = inject(ViewContainerRef);
  private readonly _focusMonitor: FocusMonitor = inject(FocusMonitor);
  private readonly _trigger: ElementRef<HTMLElement> = inject(ElementRef<HTMLElement>);
  private readonly _destroyRef: DestroyRef = inject(DestroyRef);

  popUp = input.required<MobilePopUpComponent>({ alias: 'accredibleMobilePopUpTriggerFor' });

  popUpToggled = output<boolean>();

  private _popUpClosingActions$ = Subscription.EMPTY;
  private _isPopUpOpen = false;
  private _overlayRef: OverlayRef;

  get getOverlayFirstChildEl(): Element {
    return this._overlayRef.overlayElement.children.item(0)!;
  }

  toggleMenu(): void {
    this._isPopUpOpen ? this._destroyPopUp() : this.openPopUp();
  }

  onResize(): void {
    if (
      AccredibleResponsiveHelper.isScreenGreaterThan(AccredibleResponsiveBreakpoint.XS) &&
      this._isPopUpOpen
    ) {
      this._destroyPopUp();
    }
  }

  ngOnDestroy(): void {
    if (this._overlayRef) {
      this._overlayRef.dispose();
    }
  }

  openPopUp(): void {
    this.popUpToggled.emit(true);
    this._isPopUpOpen = true;
    this._overlayRef = this._overlay.create({
      hasBackdrop: true,
      positionStrategy: this._overlay.position().global().centerHorizontally().bottom(),
      panelClass: 'mobile-popup-menu',
    });

    const templatePortal = new TemplatePortal(this.popUp().templateRef, this._viewContainerRef);
    this._overlayRef.attach(templatePortal);

    // Create focus trap for the popup
    this._configurableFocusTrapFactory.create(this._overlayRef.overlayElement);

    // We need to requestAnimationFrame in order to wait for the element to be drawn
    requestAnimationFrame(() => {
      this.getOverlayFirstChildEl.classList.add('move');
    });

    this._popUpClosingActions$ = this._popUpClosingActions()
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe(() => this._destroyPopUp());

    const firstMenuItem = this.getOverlayFirstChildEl.querySelector(
      '.mat-mdc-menu-item, .items [accredible-button-flat]',
    );

    if (firstMenuItem) {
      this._focusMonitor.focusVia(<HTMLElement>firstMenuItem, 'keyboard');
    }
  }

  private _popUpClosingActions(): Observable<MouseEvent | void> {
    const backdropClick$ = this._overlayRef.backdropClick();
    const detachment$ = this._overlayRef.detachments();
    const popUpClosed$ = this.popUp().closed$;

    return merge(backdropClick$, detachment$, popUpClosed$);
  }

  private _destroyPopUp(): void {
    this.getOverlayFirstChildEl.classList.remove('move');

    setTimeout(() => {
      if (!this._overlayRef || !this._isPopUpOpen) {
        return;
      }

      this.popUpToggled.emit(false);
      this._popUpClosingActions$.unsubscribe();
      this._isPopUpOpen = false;
      this._overlayRef.detach();

      if (this._trigger) {
        this._focusMonitor.focusVia(this._trigger, 'keyboard');
      }
    }, 500);
  }
}
